import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import config from '../config'

export default function LoginPage() {
  const { login, register } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [role, setRole] = useState('ADMIN')
  const [tenantName, setTenantName] = useState('')
  const [isRegister, setIsRegister] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [captchaSvg, setCaptchaSvg] = useState('')
  const [captchaId, setCaptchaId] = useState('')
  const [captchaValue, setCaptchaValue] = useState('')
  const [captchaLoading, setCaptchaLoading] = useState(false)
  const [captchaDisabled, setCaptchaDisabled] = useState(false)

  const fetchCaptcha = useCallback(async () => {
    setCaptchaLoading(true)
    try {
      const response = await fetch(config.getApiUrl('/auth/captcha'))
      if (!response.ok) {
        throw new Error('Failed to load captcha')
      }
      const data = await response.json()
      if (data.disabled) {
        setCaptchaDisabled(true)
        setCaptchaSvg('')
        setCaptchaId('')
      } else {
        setCaptchaDisabled(false)
        setCaptchaSvg(data.svg)
        setCaptchaId(data.captchaId)
      }
      setCaptchaValue('')
    } catch (err) {
      console.error('Unable to fetch captcha', err)
      setError((prev) => prev || 'Unable to load captcha. Please refresh the page.')
      setCaptchaSvg('')
      setCaptchaId('')
    } finally {
      setCaptchaLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchCaptcha()
  }, [fetchCaptcha])

  const onSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    if (!isRegister && !captchaDisabled && (!captchaId || !captchaValue.trim())) {
      setError('Please enter the captcha text to continue.')
      setLoading(false)
      return
    }
    
    try {
      let user
      if (isRegister) {
        user = await register(email, password, role, tenantName, rememberMe)
      } else {
        user = await login(email, password, rememberMe, {
          captchaId,
          captchaValue: captchaValue.trim(),
        })
      }
      
      if (user.role === 'SUPER_ADMIN') navigate('/super-admin')
      else if (user.role === 'ADMIN') navigate('/admin')
      else navigate('/viewer')
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
      if (!captchaDisabled) {
        fetchCaptcha()
      }
    }
  }

  return (
    <div className="modern-auth-wrap">
      <div className="modern-auth-container">
        {/* Main Title and Subtitle */}
        <div className="modern-header">
          <h1 className="modern-title">Log in to your account</h1>
          <p className="modern-subtitle">Welcome back! Please enter your details.</p>
        </div>

        {error && (
          <div className="modern-error">{error}</div>
        )}

        <form onSubmit={onSubmit} className="modern-form">
          {/* Email Field */}
          <div className="modern-field">
            <label className="modern-label">Email</label>
            <input 
              className="modern-input" 
              type="email"
              placeholder="Enter your email"
              value={email} 
              onChange={(e) => setEmail(e.target.value)} 
              required 
              disabled={loading} 
            />
          </div>

          {/* Password Field */}
          <div className="modern-field">
            <label className="modern-label">Password</label>
            <div className="modern-password-container">
              <input 
                className="modern-input modern-password-input" 
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
                required 
                disabled={loading} 
              />
              <button 
                type="button"
                className="modern-password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                disabled={loading}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {showPassword ? (
                    <>
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </>
                  ) : (
                    <>
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </>
                  )}
                </svg>
              </button>
            </div>
          </div>

          {/* Captcha */}
          {!captchaDisabled && (
            <div className="modern-field modern-captcha-field">
              <label className="modern-label">Security Check</label>
              <div className="modern-captcha-visual">
                {captchaSvg ? (
                  <div
                    className="modern-captcha-image"
                    role="img"
                    aria-label="Captcha challenge"
                    dangerouslySetInnerHTML={{ __html: captchaSvg }}
                  />
                ) : (
                  <div className="modern-captcha-placeholder">
                    {captchaLoading ? 'Loading captcha...' : 'Captcha unavailable'}
                  </div>
                )}
                <button
                  type="button"
                  className="modern-captcha-refresh"
                  onClick={fetchCaptcha}
                  disabled={captchaLoading || loading}
                >
                  {captchaLoading ? 'Refreshing...' : 'Refresh'}
                </button>
              </div>
              <input
                className="modern-input"
                type="text"
                placeholder="Enter the characters shown above"
                value={captchaValue}
                onChange={(e) => setCaptchaValue(e.target.value)}
                disabled={loading || captchaLoading}
                autoComplete="off"
              />
            </div>
          )}

          {/* Remember Me and Forgot Password */}
          <div className="modern-options">
            <label className="modern-checkbox-container">
              <input 
                type="checkbox" 
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={loading}
              />
              <span className="modern-checkbox-label">Remember me</span>
            </label>
            {/* <button type="button" className="modern-forgot-link">
              Forgot password
            </button> */}
          </div>

          {/* Sign In Button */}
          <button type="submit" className="modern-signin-btn" disabled={loading}>
            {loading ? 'Please wait...' : 'Sign in'}
          </button>

          {/* Google Sign In Button */}
          {/* <button type="button" className="modern-google-btn" disabled={loading}>
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign in with Google
          </button> */}

          {/* Register Link */}
          {/* <div className="modern-footer">
            <span className="modern-footer-text">Don't have an account?</span>
            <button 
              type="button" 
              className="modern-signup-link"
              onClick={() => setIsRegister(!isRegister)}
              disabled={loading}
            >
              Sign up
            </button>
          </div> */}
        </form>

        {/* Trademark Footer */}
        <div className="modern-trademark-footer">
          <div className="powered-by-stedaxis">
            <span className="powered-by-text">Powered by</span>
            <span className="stedaxis-text">STEDAXIS</span>
          </div>
        </div>

        {/* Registration Form (Hidden by default) */}
        {isRegister && (
          <div className="modern-register-overlay">
            <div className="modern-register-form">
              <h2 className="modern-title">Create account</h2>
              <p className="modern-subtitle">Please fill in your details to create an account.</p>
              
              <form onSubmit={onSubmit} className="modern-form">
                <div className="modern-field">
                  <label className="modern-label">Email</label>
                  <input 
                    className="modern-input" 
                    type="email"
                    placeholder="Enter your email"
                    value={email} 
                    onChange={(e) => setEmail(e.target.value)} 
                    required 
                    disabled={loading} 
                  />
                </div>

                <div className="modern-field">
                  <label className="modern-label">Password</label>
                  <input 
                    className="modern-input" 
                    type="password"
                    placeholder="Create a password"
                    value={password} 
                    onChange={(e) => setPassword(e.target.value)} 
                    required 
                    disabled={loading} 
                  />
                </div>

                <div className="modern-field">
                  <label className="modern-label">Role</label>
                  <select 
                    className="modern-select" 
                    value={role} 
                    onChange={(e) => setRole(e.target.value)} 
                    disabled={loading}
                  >
                    <option value="SUPER_ADMIN">Super Admin</option>
                    <option value="ADMIN">Admin</option>
                  </select>
                </div>

                {role === 'ADMIN' && (
                  <div className="modern-field">
                    <label className="modern-label">Company Name</label>
                    <input 
                      className="modern-input" 
                      placeholder="Your company name"
                      value={tenantName} 
                      onChange={(e) => setTenantName(e.target.value)} 
                      disabled={loading} 
                    />
                  </div>
                )}

                <button type="submit" className="modern-signin-btn" disabled={loading}>
                  {loading ? 'Please wait...' : 'Create account'}
                </button>

                 <div className="modern-footer">
                   <span className="modern-footer-text">Already have an account?</span>
                   <button 
                     type="button" 
                     className="modern-signup-link"
                     onClick={() => setIsRegister(false)}
                     disabled={loading}
                   >
                     Sign in
                   </button>
                 </div>
               </form>
               
               {/* Trademark Footer */}
               <div className="modern-trademark-footer">
                 <div className="powered-by-stedaxis">
                   <span className="powered-by-text">Powered by</span>
                   <span className="stedaxis-text">STEDAXIS</span>
                 </div>
               </div>
             </div>
           </div>
        )}
      </div>
    </div>
  )
}


